import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api from "../../lib/axios";
import { toast } from "sonner";
import {
  FileText,
  Plus,
  Copy,
  Trash2,
  FolderOpen,
  Search,
  Calendar,
  Building2,
  User,
  DollarSign,
  MoreVertical,
  RefreshCw,
  Import,
  ArrowRight,
  Clock,
  Send,
  CheckCircle2,
  XCircle,
  Bell,
  AlertTriangle,
  CalendarX,
  Download,
  CheckSquare,
  Square,
  Eye,
  GitCompare
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import NewQuotationModal from "./NewQuotationModal";
import QuotationEditorModal from "./QuotationEditorModal";
import { PDFPreviewModal } from "./PDFPreviewModal";
import RevisionComparator from "./RevisionComparator";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value, currency = "USD") => {
  return new Intl.NumberFormat('es-PE', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2
  }).format(value || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return date.toLocaleDateString('es-PE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const getStatusBadge = (status) => {
  const statusConfig = {
    draft: { label: "Borrador", variant: "secondary", icon: Clock },
    sent: { label: "Enviada", variant: "default", icon: Send },
    approved: { label: "Aprobada", variant: "success", icon: CheckCircle2 },
    rejected: { label: "Rechazada", variant: "destructive", icon: XCircle }
  };
  const config = statusConfig[status] || statusConfig.draft;
  const Icon = config.icon;
  return (
    <Badge variant={config.variant} className="flex items-center gap-1">
      <Icon className="w-3 h-3" />
      {config.label}
    </Badge>
  );
};

const QuotationView = () => {
  const { code } = useParams();  // Get quotation code from URL
  const navigate = useNavigate();
  const location = useLocation();
  
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [quotationToDelete, setQuotationToDelete] = useState(null);
  
  // Migration state
  const [isMigrationModalOpen, setIsMigrationModalOpen] = useState(false);
  const [oldProjects, setOldProjects] = useState([]);
  const [loadingOldProjects, setLoadingOldProjects] = useState(false);
  const [migratingProjectId, setMigratingProjectId] = useState(null);

  // Alerts state
  const [isAlertsModalOpen, setIsAlertsModalOpen] = useState(false);
  const [expiringQuotations, setExpiringQuotations] = useState([]);
  const [expiredQuotations, setExpiredQuotations] = useState([]);
  const [totalAlerts, setTotalAlerts] = useState(0);

  // Batch export state
  const [selectedForExport, setSelectedForExport] = useState([]);
  const [isExporting, setIsExporting] = useState(false);

  // PDF Preview state
  const [isPdfPreviewOpen, setIsPdfPreviewOpen] = useState(false);
  const [previewQuotationId, setPreviewQuotationId] = useState(null);
  const [previewQuotationCode, setPreviewQuotationCode] = useState('');

  // Comparison mode state
  const [isCompareMode, setIsCompareMode] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState([]);
  const [isComparatorOpen, setIsComparatorOpen] = useState(false);

  // Fetch quotations
  const fetchQuotations = async () => {
    setLoading(true);
    try {
      const response = await api.get(`${API}/quotations`);
      setQuotations(response.data.quotations || []);
    } catch (error) {
      // console.error("Error fetching quotations:", error);
      toast.error("Error al cargar cotizaciones");
    } finally {
      setLoading(false);
    }
  };

  // Fetch expiring quotations
  const fetchAlerts = async () => {
    try {
      const response = await api.get(`${API}/quotations/alerts/expiring?days=7`);
      setExpiringQuotations(response.data.expiring || []);
      setExpiredQuotations(response.data.expired || []);
      setTotalAlerts(response.data.total_alerts || 0);
    } catch (error) {
      console.error("Error fetching alerts:", error);
    }
  };

  // Fetch old projects for migration
  const fetchOldProjects = async () => {
    setLoadingOldProjects(true);
    try {
      const response = await api.get(`${API}/quotations/migrate/old-projects`);
      setOldProjects(response.data.projects || []);
    } catch (error) {
      // console.error("Error fetching old projects:", error);
      toast.error("Error al cargar proyectos antiguos");
    } finally {
      setLoadingOldProjects(false);
    }
  };

  // Handle migrate project
  const handleMigrateProject = async (projectId) => {
    setMigratingProjectId(projectId);
    try {
      const response = await api.post(`${API}/quotations/migrate/from-project/${projectId}`);
      toast.success(response.data.message);
      fetchQuotations();
      fetchOldProjects(); // Refresh to show migrated status
    } catch (error) {
      // console.error("Error migrating project:", error);
      const message = error.response?.data?.detail || "Error al migrar proyecto";
      toast.error(message);
    } finally {
      setMigratingProjectId(null);
    }
  };

  // Open migration modal
  const handleOpenMigration = () => {
    setIsMigrationModalOpen(true);
    fetchOldProjects();
  };

  useEffect(() => {
    fetchQuotations();
    fetchAlerts();
  }, []);

  // Deep link: Open quotation from URL parameter
  useEffect(() => {
    if (code && quotations.length > 0 && !isEditorOpen) {
      // Find quotation by code
      const quotation = quotations.find(q => 
        q.code?.toLowerCase() === code.toLowerCase() ||
        q.id === code
      );
      if (quotation) {
        setSelectedQuotation(quotation);
        setIsEditorOpen(true);
      } else {
        toast.error(`Cotización ${code} no encontrada`);
        navigate('/quotation', { replace: true });
      }
    }
  }, [code, quotations, isEditorOpen, navigate]);

  // Filter quotations by search
  const filteredQuotations = quotations.filter(q => 
    q.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    q.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    q.client_company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    q.client_contact?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle open quotation - update URL when opening
  const handleOpenQuotation = (quotation) => {
    setSelectedQuotation(quotation);
    setIsEditorOpen(true);
    // Update URL with quotation code for shareable deep link
    navigate(`/quotation/${quotation.code}`, { replace: true });
  };

  // Handle close quotation editor - reset URL
  const handleCloseEditor = () => {
    setSelectedQuotation(null);
    setIsEditorOpen(false);
    navigate('/quotation', { replace: true });
    fetchQuotations(); // Refresh list after editing
  };

  // Handle duplicate quotation
  const handleDuplicate = async (quotation) => {
    try {
      const response = await api.post(`${API}/quotations/${quotation.id}/duplicate`);
      toast.success(`Cotización duplicada: ${response.data.quotation.code}`);
      fetchQuotations();
    } catch (error) {
      // console.error("Error duplicating quotation:", error);
      toast.error("Error al duplicar cotización");
    }
  };

  // Handle delete quotation
  const handleDelete = async () => {
    if (!quotationToDelete) return;
    try {
      await api.delete(`${API}/quotations/${quotationToDelete.id}`);
      toast.success("Cotización eliminada");
      setDeleteDialogOpen(false);
      setQuotationToDelete(null);
      fetchQuotations();
    } catch (error) {
      // console.error("Error deleting quotation:", error);
      toast.error("Error al eliminar cotización");
    }
  };

  // Handle new quotation created
  const handleQuotationCreated = (quotation) => {
    setIsNewModalOpen(false);
    setSelectedQuotation(quotation);
    setIsEditorOpen(true);
    fetchQuotations();
  };

  // Handle quotation updated
  const handleQuotationUpdated = () => {
    fetchQuotations();
  };

  // Handle toggle selection for batch export
  const handleToggleSelection = (quotationId) => {
    setSelectedForExport(prev => {
      if (prev.includes(quotationId)) {
        return prev.filter(id => id !== quotationId);
      }
      return [...prev, quotationId];
    });
  };

  // Handle select all for batch export
  const handleSelectAll = () => {
    if (selectedForExport.length === filteredQuotations.length) {
      setSelectedForExport([]);
    } else {
      setSelectedForExport(filteredQuotations.map(q => q.id));
    }
  };

  // Handle compare mode toggle
  const toggleCompareMode = () => {
    setIsCompareMode(!isCompareMode);
    setSelectedForCompare([]);
  };

  // Handle selection for comparison
  const handleToggleCompareSelection = (quotationId) => {
    setSelectedForCompare(prev => {
      if (prev.includes(quotationId)) {
        return prev.filter(id => id !== quotationId);
      }
      // Maximum 2 selections for comparison
      if (prev.length >= 2) {
        return [prev[1], quotationId]; // Replace oldest selection
      }
      return [...prev, quotationId];
    });
  };

  // Open comparator when 2 quotations selected
  const handleOpenComparator = () => {
    if (selectedForCompare.length !== 2) {
      toast.error("Selecciona exactamente 2 cotizaciones para comparar");
      return;
    }
    setIsComparatorOpen(true);
  };

  // Handle batch PDF export
  const handleBatchExport = async () => {
    if (selectedForExport.length === 0) {
      toast.error("Selecciona al menos una cotización");
      return;
    }
    
    setIsExporting(true);
    try {
      const response = await api.post(
        `${API}/quotations/export-batch-pdf`,
        { quotation_ids: selectedForExport },
        { responseType: 'blob' }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'cotizaciones_lote.zip';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${selectedForExport.length} PDFs exportados correctamente`);
      setSelectedForExport([]); // Clear selection after export
    } catch (error) {
      console.error("Error exporting PDFs:", error);
      toast.error("Error al exportar PDFs");
    } finally {
      setIsExporting(false);
    }
  };

  // Handle open PDF preview
  const handleOpenPdfPreview = (quotation) => {
    setPreviewQuotationId(quotation.id);
    setPreviewQuotationCode(quotation.code);
    setIsPdfPreviewOpen(true);
  };

  // Handle close PDF preview
  const handleClosePdfPreview = () => {
    setIsPdfPreviewOpen(false);
    setPreviewQuotationId(null);
    setPreviewQuotationCode('');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Cotizaciones</h1>
          <p className="text-slate-500">Gestiona tus cotizaciones y proyectos</p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            size="sm"
            onClick={fetchQuotations}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          <Button 
            variant={totalAlerts > 0 ? "default" : "outline"}
            size="sm"
            onClick={() => setIsAlertsModalOpen(true)}
            className={totalAlerts > 0 ? "bg-orange-500 hover:bg-orange-600" : ""}
          >
            <Bell className="w-4 h-4 mr-2" />
            Alertas
            {totalAlerts > 0 && (
              <span className="ml-2 bg-white text-orange-600 text-xs font-bold px-2 py-0.5 rounded-full">
                {totalAlerts}
              </span>
            )}
          </Button>
          <Button 
            variant="outline"
            size="sm"
            onClick={handleOpenMigration}
          >
            <Import className="w-4 h-4 mr-2" />
            Importar Proyectos
          </Button>
          {selectedForExport.length > 0 && (
            <Button 
              variant="outline"
              size="sm"
              onClick={handleBatchExport}
              disabled={isExporting}
              className="border-green-500 text-green-600 hover:bg-green-50"
              data-testid="batch-export-btn"
            >
              <Download className={`w-4 h-4 mr-2 ${isExporting ? 'animate-bounce' : ''}`} />
              Exportar PDFs ({selectedForExport.length})
            </Button>
          )}
          <Button 
            variant={isCompareMode ? "default" : "outline"}
            size="sm"
            onClick={toggleCompareMode}
            className={isCompareMode ? "bg-indigo-600 hover:bg-indigo-700" : "border-indigo-500 text-indigo-600 hover:bg-indigo-50"}
            data-testid="compare-mode-btn"
          >
            <GitCompare className="w-4 h-4 mr-2" />
            {isCompareMode ? `Comparar (${selectedForCompare.length}/2)` : "Comparar"}
          </Button>
          {isCompareMode && selectedForCompare.length === 2 && (
            <Button 
              size="sm"
              onClick={handleOpenComparator}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="open-comparator-btn"
            >
              <ArrowRight className="w-4 h-4 mr-2" />
              Ver Diferencias
            </Button>
          )}
          <Button 
            onClick={() => setIsNewModalOpen(true)}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="new-quotation-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Proyecto
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Cotizaciones</p>
                <p className="text-2xl font-bold">{quotations.length}</p>
              </div>
              <FileText className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Borradores</p>
                <p className="text-2xl font-bold">
                  {quotations.filter(q => q.status === 'draft').length}
                </p>
              </div>
              <FileText className="w-8 h-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Enviadas</p>
                <p className="text-2xl font-bold">
                  {quotations.filter(q => q.status === 'sent').length}
                </p>
              </div>
              <FileText className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Aprobadas</p>
                <p className="text-2xl font-bold">
                  {quotations.filter(q => q.status === 'approved').length}
                </p>
              </div>
              <FileText className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Table */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Lista de Cotizaciones</CardTitle>
              <CardDescription>
                {filteredQuotations.length} cotizaciones encontradas
              </CardDescription>
            </div>
            <div className="relative w-72">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por código, nombre, cliente..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="quotation-search"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : filteredQuotations.length === 0 ? (
            <div className="text-center py-12">
              <FolderOpen className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No hay cotizaciones</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => setIsNewModalOpen(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Crear primera cotización
              </Button>
            </div>
          ) : (
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleSelectAll(); }}
                        className="p-1 hover:bg-slate-100 rounded"
                        data-testid="select-all-checkbox"
                      >
                        {selectedForExport.length === filteredQuotations.length && filteredQuotations.length > 0 ? (
                          <CheckSquare className="w-4 h-4 text-blue-600" />
                        ) : (
                          <Square className="w-4 h-4 text-slate-400" />
                        )}
                      </button>
                    </TableHead>
                    <TableHead className="w-28">Código</TableHead>
                    <TableHead>Proyecto</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead className="w-28">Fecha</TableHead>
                    <TableHead className="w-20 text-center">Items</TableHead>
                    <TableHead className="w-28 text-right">Total m²</TableHead>
                    <TableHead className="w-32 text-right">Total USD</TableHead>
                    <TableHead className="w-24 text-center">Estado</TableHead>
                    <TableHead className="w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredQuotations.map((quotation) => (
                    <TableRow 
                      key={quotation.id}
                      className={`cursor-pointer hover:bg-slate-50 ${
                        selectedForExport.includes(quotation.id) ? 'bg-blue-50' : ''
                      } ${
                        isCompareMode && selectedForCompare.includes(quotation.id) ? 'bg-indigo-50 ring-1 ring-indigo-300' : ''
                      }`}
                      onClick={() => isCompareMode ? handleToggleCompareSelection(quotation.id) : handleOpenQuotation(quotation)}
                      data-testid={`quotation-row-${quotation.code}`}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {isCompareMode ? (
                          <button
                            onClick={() => handleToggleCompareSelection(quotation.id)}
                            className="p-1 hover:bg-indigo-100 rounded"
                            data-testid={`compare-checkbox-${quotation.code}`}
                          >
                            {selectedForCompare.includes(quotation.id) ? (
                              <CheckSquare className="w-4 h-4 text-indigo-600" />
                            ) : (
                              <Square className="w-4 h-4 text-slate-400" />
                            )}
                          </button>
                        ) : (
                          <button
                            onClick={() => handleToggleSelection(quotation.id)}
                            className="p-1 hover:bg-slate-100 rounded"
                            data-testid={`select-checkbox-${quotation.code}`}
                          >
                            {selectedForExport.includes(quotation.id) ? (
                              <CheckSquare className="w-4 h-4 text-blue-600" />
                            ) : (
                              <Square className="w-4 h-4 text-slate-400" />
                            )}
                          </button>
                        )}
                      </TableCell>
                      <TableCell className="font-mono font-medium text-blue-600">
                        {quotation.code}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{quotation.name}</div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-slate-400" />
                          <div>
                            <div className="text-sm">{quotation.client_company || "-"}</div>
                            {quotation.client_contact && (
                              <div className="text-xs text-slate-500 flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {quotation.client_contact}
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          {formatDate(quotation.quote_date)}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        {quotation.items?.length || 0}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {(quotation.total_m2 || 0).toFixed(2)} m²
                      </TableCell>
                      <TableCell className="text-right font-mono font-medium">
                        {formatCurrency(quotation.subtotal_supply)}
                      </TableCell>
                      <TableCell className="text-center">
                        {getStatusBadge(quotation.status)}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              handleOpenQuotation(quotation);
                            }}>
                              <FolderOpen className="w-4 h-4 mr-2" />
                              Abrir
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              handleOpenPdfPreview(quotation);
                            }}>
                              <Eye className="w-4 h-4 mr-2" />
                              Ver PDF
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              handleDuplicate(quotation);
                            }}>
                              <Copy className="w-4 h-4 mr-2" />
                              Duplicar
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              className="text-red-600"
                              onClick={(e) => {
                                e.stopPropagation();
                                setQuotationToDelete(quotation);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Eliminar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Quotation Modal */}
      <NewQuotationModal
        isOpen={isNewModalOpen}
        onClose={() => setIsNewModalOpen(false)}
        onCreated={handleQuotationCreated}
      />

      {/* Quotation Editor Modal */}
      {selectedQuotation && (
        <QuotationEditorModal
          isOpen={isEditorOpen}
          onClose={handleCloseEditor}
          quotation={selectedQuotation}
          onUpdated={handleQuotationUpdated}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar cotización?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción eliminará permanentemente la cotización{" "}
              <span className="font-semibold">{quotationToDelete?.code}</span> y 
              todos sus elementos. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Migration Modal */}
      <Dialog open={isMigrationModalOpen} onOpenChange={setIsMigrationModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Import className="w-5 h-5 text-blue-600" />
              Importar Proyectos Anteriores
            </DialogTitle>
            <DialogDescription>
              Migra proyectos guardados en el sistema anterior a la nueva estructura de cotizaciones.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {loadingOldProjects ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : oldProjects.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <FolderOpen className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                <p>No hay proyectos anteriores para migrar</p>
              </div>
            ) : (
              <div className="border rounded-lg max-h-80 overflow-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-white">
                    <TableRow>
                      <TableHead>Nombre</TableHead>
                      <TableHead className="w-20 text-right">Items</TableHead>
                      <TableHead className="w-28 text-right">Total USD</TableHead>
                      <TableHead className="w-24"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {oldProjects.map((project) => {
                      // Check if already migrated
                      const isMigrated = quotations.some(q => q.migrated_from === project.id);
                      return (
                        <TableRow key={project.id}>
                          <TableCell>
                            <div className="font-medium">{project.name}</div>
                            {project.updated_at && (
                              <div className="text-xs text-slate-500">
                                {formatDate(project.updated_at)}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {project.items?.length || 0}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(project.total_supply)}
                          </TableCell>
                          <TableCell>
                            {isMigrated ? (
                              <Badge variant="success" className="flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3" />
                                Migrado
                              </Badge>
                            ) : (
                              <Button
                                size="sm"
                                onClick={() => handleMigrateProject(project.id)}
                                disabled={migratingProjectId === project.id}
                                className="bg-blue-600 hover:bg-blue-700"
                              >
                                {migratingProjectId === project.id ? (
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <ArrowRight className="w-4 h-4 mr-1" />
                                    Migrar
                                  </>
                                )}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Alerts Modal */}
      <Dialog open={isAlertsModalOpen} onOpenChange={setIsAlertsModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-orange-500" />
              Alertas de Vencimiento
            </DialogTitle>
            <DialogDescription>
              Cotizaciones que vencen pronto o ya vencieron
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Expired */}
            {expiredQuotations.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-red-600 flex items-center gap-2 mb-2">
                  <CalendarX className="w-4 h-4" />
                  Vencidas ({expiredQuotations.length})
                </h3>
                <div className="space-y-2">
                  {expiredQuotations.map((q) => (
                    <div 
                      key={q.id}
                      className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg cursor-pointer hover:bg-red-100 transition-colors"
                      onClick={() => {
                        setSelectedQuotation(q);
                        setIsEditorOpen(true);
                        setIsAlertsModalOpen(false);
                      }}
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-red-600 font-medium">{q.code}</span>
                          <span className="text-sm">{q.name}</span>
                        </div>
                        <p className="text-xs text-red-500">
                          Venció hace {Math.abs(q.days_until_expiry)} días
                        </p>
                      </div>
                      <Badge variant="destructive">Vencida</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Expiring Soon */}
            {expiringQuotations.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-orange-600 flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4" />
                  Por Vencer ({expiringQuotations.length})
                </h3>
                <div className="space-y-2">
                  {expiringQuotations.map((q) => (
                    <div 
                      key={q.id}
                      className="flex items-center justify-between p-3 bg-orange-50 border border-orange-200 rounded-lg cursor-pointer hover:bg-orange-100 transition-colors"
                      onClick={() => {
                        setSelectedQuotation(q);
                        setIsEditorOpen(true);
                        setIsAlertsModalOpen(false);
                      }}
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-orange-600 font-medium">{q.code}</span>
                          <span className="text-sm">{q.name}</span>
                        </div>
                        <p className="text-xs text-orange-500">
                          {q.days_until_expiry === 0 
                            ? "Vence hoy" 
                            : `Vence en ${q.days_until_expiry} día${q.days_until_expiry > 1 ? 's' : ''}`
                          }
                        </p>
                      </div>
                      <Badge className="bg-orange-500">Por vencer</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* No alerts */}
            {totalAlerts === 0 && (
              <div className="text-center py-8 text-slate-500">
                <CheckCircle2 className="w-12 h-12 mx-auto text-green-400 mb-2" />
                <p>No hay cotizaciones por vencer</p>
                <p className="text-sm">Todas las cotizaciones están al día</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* PDF Preview Modal */}
      <PDFPreviewModal
        isOpen={isPdfPreviewOpen}
        onClose={handleClosePdfPreview}
        quotationId={previewQuotationId}
        quotationCode={previewQuotationCode}
      />

      {/* Revision Comparator Modal */}
      <RevisionComparator
        isOpen={isComparatorOpen}
        onClose={() => {
          setIsComparatorOpen(false);
          setIsCompareMode(false);
          setSelectedForCompare([]);
        }}
        quotation1={quotations.find(q => q.id === selectedForCompare[0])}
        quotation2={quotations.find(q => q.id === selectedForCompare[1])}
        allQuotations={quotations}
      />
    </div>
  );
};

export default QuotationView;
